import { createRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Check, ChevronLeft, ChevronRight, Hourglass } from 'lucide-react'
import rootRoute from '../rootRoute'
import { EventDetail } from '../EventDetailPage'
import AdminTabs from './AdminTabs'
import type { Event } from '@/lib/types'
import type { components } from '@/lib/client'
import {
  Button,
  Card,
  Row,
  Stack,
  errorToast,
  spacing,
  toast,
} from '@/components/ui'
import DateWarning from '@/components/DateWarning'
import Loader from '@/components/Loader'
import { catOf } from '@/lib/types'
import { $api, $client } from '@/lib/fetch'
import { adminRouteLoader } from '@/lib/auth'
import { EventStatus } from '@/lib/constants'
import { isLikelyInvalidDate, prettyDate, splitAddress } from '@/lib/helpers'

type CreateEvent = components['schemas']['CreateEvent']

function eventToCreate(event: Event, status: string): CreateEvent {
  return {
    title: event.title,
    description: event.description,
    address: event.location.address,
    type: event.type.type,
    status,
    start_date: event.start_date,
    end_date: event.end_date,
    external_link: event.external_link,
    entity_id: event.entity?.id,
  }
}

function QueueItem({
  event,
  selected,
  onSelect,
}: {
  event: Event
  selected: boolean
  onSelect: () => void
}) {
  const cat = catOf(event.type.type)
  const { city } = splitAddress(event.location.address)
  return (
    <button
      onClick={onSelect}
      style={{
        flexShrink: 0,
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        gap: spacing.sm,
        alignItems: 'center',
        padding: `${spacing.sm} ${spacing.md}`,
        width: 240,
        borderRadius: 'var(--radius-md)',
        border: `1.5px solid ${selected ? 'var(--emerald-600)' : 'var(--border-soft)'}`,
        background: selected ? 'var(--surface-tint)' : 'var(--surface-card)',
        boxShadow: selected ? 'var(--shadow-sm)' : 'none',
      }}
    >
      <span
        style={{
          width: 5,
          alignSelf: 'stretch',
          borderRadius: 3,
          background: cat.color,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            color: 'var(--ink-0)',
            fontSize: 14.5,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {event.title}
        </div>
        <Row
          gap="sm"
          style={{
            fontSize: 12.5,
            color: 'var(--ink-3)',
            marginTop: 4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {city ? `${city} · ` : ''}
          {isLikelyInvalidDate(event.start_date) ? (
            <DateWarning compact />
          ) : (
            prettyDate(event.start_date)
          )}
        </Row>
      </div>
    </button>
  )
}

const AdminEventsView = () => {
  const navigate = useNavigate()
  const { id, last_id, last_ts } = adminEventViewRoute.useSearch()

  const pageToken =
    last_id != null && last_ts != null
      ? { last_id: last_id, last_start_timestamp_ms: last_ts }
      : undefined

  const { data, isLoading, refetch } = $api.useQuery(
    'post',
    '/api/events/admin-search',
    {
      body: {
        status: EventStatus.NotVerified,
        limit: 20,
        page_token: pageToken,
        order: 'asc',
      },
    },
  )
  const [busy, setBusy] = useState(false)

  const nextToken = data?.next_page_token
  const goNext = () => {
    if (!nextToken) return
    navigate({
      to: '/admin/events',
      search: {
        last_id: nextToken.last_id,
        last_ts: nextToken.last_start_timestamp_ms,
      },
    } as never)
  }
  const goPrev = () => window.history.back()

  const pending: Array<Event> = data?.events ?? []
  const fromPending = pending.find((e) => e.id === id)
  const { data: fetchedById } = $api.useQuery(
    'get',
    '/api/events/id/{id}',
    { params: { path: { id: id ?? 0 } } },
    { enabled: id != null && !fromPending },
  )
  const selEvent = fromPending ?? fetchedById?.event ?? pending.at(0)

  const selectEvent = (eventId: number) => {
    navigate({
      to: '/admin/events',
      search: { id: eventId, last_id, last_ts },
      resetScroll: false,
    } as never)
  }

  useEffect(() => {
    if (id == null && selEvent) {
      navigate({
        to: '/admin/events',
        search: { id: selEvent.id, last_id, last_ts },
        replace: true,
        resetScroll: false,
      } as never)
    }
  }, [selEvent, id, last_id, last_ts, navigate])

  const decide = async (event: Event, status: string) => {
    const currentIdx = pending.findIndex((e) => e.id === event.id)
    const nextId =
      currentIdx >= 0 ? pending.at(currentIdx + 1)?.id : pending.at(0)?.id

    setBusy(true)
    const { error } = await $client
      .PATCH('/api/events/id/{id}', {
        params: { path: { id: event.id } },
        body: { event: eventToCreate(event, status) },
      })
      .finally(() => setBusy(false))

    if (error) {
      errorToast('Échec de la mise à jour', error)
      return
    }
    toast({
      color: 'success',
      title:
        status === EventStatus.Verified
          ? 'Événement approuvé'
          : 'Événement refusé',
    })
    navigate({
      to: '/admin/events',
      search: { id: nextId, last_id, last_ts },
      replace: true,
      resetScroll: false,
    } as never)
    await refetch()
  }

  return (
    <div
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--space-8) var(--gutter) var(--space-20)',
      }}
    >
      <AdminTabs />
      <div className="oumma-overline" style={{ marginBottom: spacing.sm }}>
        Espace admin
      </div>
      <h1 style={{ fontSize: 'var(--text-h1)', margin: '0 0 24px' }}>
        Validation des événements
      </h1>

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-soft)',
          borderRadius: 'var(--radius-pill)',
          boxShadow: 'var(--shadow-xs)',
          marginBottom: spacing['2xl'],
          fontSize: 14.5,
          color: 'var(--ink-2)',
        }}
      >
        <Hourglass size={17} style={{ color: 'var(--gold-600)' }} />
        <strong
          style={{ color: 'var(--ink-0)', fontFamily: 'var(--font-mono)' }}
        >
          {pending.length}
        </strong>{' '}
        événement{pending.length > 1 ? 's' : ''} en attente de validation
      </div>

      {isLoading ? (
        <div style={{ padding: 'var(--space-16)', textAlign: 'center' }}>
          Chargement…
        </div>
      ) : !selEvent ? (
        <Card padding={40}>
          <div style={{ textAlign: 'center' }}>
            <Row
              justify="center"
              style={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                margin: '0 auto 20px',
                background: 'var(--emerald-50)',
                color: 'var(--emerald-700)',
                border: '1px solid var(--emerald-100)',
              }}
            >
              <Check size={36} />
            </Row>
            <h2 style={{ fontSize: 'var(--text-h3)', margin: '0 0 8px' }}>
              La file est vide — barak Allahu fik
            </h2>
            <p style={{ fontSize: 16, color: 'var(--ink-2)' }}>
              Tous les événements en attente ont été traités.
            </p>
          </div>
        </Card>
      ) : (
        <Stack gap="lg">
          <Row
            gap="sm"
            style={{
              overflowX: 'auto',
              flexWrap: 'nowrap',
              paddingBottom: spacing.xs,
            }}
          >
            {pending.map((e) => (
              <QueueItem
                key={e.id}
                event={e}
                selected={selEvent.id === e.id}
                onSelect={() => selectEvent(e.id)}
              />
            ))}
          </Row>

          {busy ? (
            <Loader />
          ) : (
            <EventDetail
              event={selEvent}
              isAdmin
              actionBusy={busy}
              onApprove={() => decide(selEvent, EventStatus.Verified)}
              onReject={() => decide(selEvent, EventStatus.SoftDelete)}
            />
          )}
        </Stack>
      )}

      {(last_id != null || nextToken) && (
        <Row gap="sm" justify="flex-end" style={{ marginTop: spacing.lg }}>
          <Button
            size="sm"
            variant="secondary"
            iconLeft={<ChevronLeft size={15} />}
            onClick={goPrev}
            disabled={last_id == null}
          >
            Précédent
          </Button>
          <Button
            size="sm"
            variant="secondary"
            iconRight={<ChevronRight size={15} />}
            onClick={goNext}
            disabled={!nextToken}
          >
            Suivant
          </Button>
        </Row>
      )}
    </div>
  )
}

export const adminEventViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/events',
  validateSearch: (s: Record<string, unknown>) => ({
    id: s.id ? Number(s.id) : undefined,
    last_id: s.last_id ? Number(s.last_id) : undefined,
    last_ts: s.last_ts ? Number(s.last_ts) : undefined,
  }),
  loader: adminRouteLoader,
}).lazy(() => import('./AdminEventsView.lazy').then((m) => m.Route))

export default AdminEventsView
